import 'arrive';
import Swiper from 'swiper/bundle';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/* ----------------------------------------
 * Registry
 * ---------------------------------------- */

const counterRegistry = new WeakMap();

/* ----------------------------------------
 * Utilities
 * ---------------------------------------- */

function formatNumber(value, decimals = 0, sep = '.') {
  const n = Number(value);
  if (!isFinite(n)) return String(value);

  const fixed = n.toFixed(decimals);
  const [int, dec] = fixed.split('.');
  const withSep = int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);

  return dec ? `${withSep},${dec}` : withSep;
}

function parseNumberFromText(el) {
  const raw = (el.textContent || '').trim();
  if (!raw) return 0;

  const cleaned = raw.replace(/[^0-9.,-]/g, '');

  if (cleaned.includes(',') && cleaned.includes('.')) {
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
  }

  return parseFloat(cleaned.replace(',', '.')) || 0;
}

/* ----------------------------------------
 * Wheel DOM builder
 * ---------------------------------------- */

function buildWheel(el, formattedValue) {
  el.innerHTML = '';
  el.classList.add('counter--wheel');

  const container = document.createElement('span');
  container.className = 'counter-wheel';
  el.appendChild(container);

  const cols = [];
  const DIGITS = '0123456789';
  const REPEAT = 3;

  formattedValue.split('').forEach((char) => {
    const col = document.createElement('span');
    col.className = 'counter-wheel__col';

    const track = document.createElement('span');
    track.className = 'counter-wheel__track';

    if (DIGITS.includes(char)) {
      for (let r = 0; r < REPEAT + 1; r++) {
        for (let d = 0; d < 10; d++) {
          const cell = document.createElement('span');
          cell.className = 'counter-wheel__digit';
          cell.textContent = d;
          track.appendChild(cell);
        }
      }
    } else {
      const cell = document.createElement('span');
      cell.className = 'counter-wheel__digit is-static';
      cell.textContent = char;
      track.appendChild(cell);
    }

    col.appendChild(track);
    container.appendChild(col);

    cols.push({ col, track, isDigit: DIGITS.includes(char) });
  });

  return cols;
}

/* ----------------------------------------
 * Counter initialization
 * ---------------------------------------- */

function initCounters(block) {
  const counters = block.querySelectorAll('.counter');
  const tweens = [];

  counters.forEach((el) => {
    if (el.dataset.counterInitialized === 'true') return;
    el.dataset.counterInitialized = 'true';

    const isWheel =
      el.dataset.wheel === 'true' || el.classList.contains('counter--wheel');

    const end =
      parseFloat(el.dataset.target) ||
      parseNumberFromText(el) ||
      0;

    const start = parseFloat(el.dataset.start || '0');
    const duration = parseFloat(el.dataset.duration || '2.5');
    const decimals = parseInt(el.dataset.decimals || '0', 10);
    const sep = el.dataset.sep || '.';

    /* ---------- CLASSIC ---------- */

    if (!isWheel) {
      const obj = { val: start };
      el.textContent = formatNumber(start, decimals, sep);

      tweens.push(
        gsap.to(obj, {
          val: end,
          duration,
          ease: 'power2.out',
          paused: true,
          onUpdate: () => {
            el.textContent = formatNumber(obj.val, decimals, sep);
          },
          onComplete: () => {
            el.textContent = formatNumber(end, decimals, sep);
          },
        })
      );

      return;
    }

    /* ---------- WHEEL ---------- */

    const endInt = Math.max(0, Math.floor(end));
    const endStr = formatNumber(endInt, decimals, sep);

    const cols = buildWheel(el, endStr);
    const digitCols = cols.filter(c => c.isDigit);

    const digitHeight =
      el.querySelector('.counter-wheel__digit')?.getBoundingClientRect().height || 0;

    const tracks = digitCols.map(c => c.track).reverse();
    tracks.forEach(t => gsap.set(t, { y: 0, force3D: true }));

    const progress = { step: 0 };

    tweens.push(
      gsap.to(progress, {
        step: endInt,
        duration,
        ease: 'power2.inOut',
        paused: true,
        onUpdate: () => {
          const s = progress.step;

          tracks.forEach((track, i) => {
            const val = s / Math.pow(10, i);
            const base = Math.floor(val);
            const frac = val - base;
            const digit = (base % 10 + frac) % 10;

            gsap.set(track, {
              y: -digit * digitHeight,
              force3D: true,
            });
          });
        },
        onComplete: () => {
          tracks.forEach((track, i) => {
            const d = Math.floor(endInt / Math.pow(10, i)) % 10;
            gsap.set(track, { y: -d * digitHeight, force3D: true });
          });

          el.setAttribute('aria-label', endStr);
        },
      })
    );
  });

  return tweens;
}

/* ----------------------------------------
 * ScrollTrigger binding
 * ---------------------------------------- */

document.arrive('.block-simple-counters', { existing: true }, (block) => {
  const tweens = initCounters(block);
  if (!tweens.length) return;

  const trigger = ScrollTrigger.create({
    trigger: block,
    start: 'top center',
    once: true,
    onEnter: () => tweens.forEach(t => t.play()),
  });

  counterRegistry.set(block, { tweens, trigger });
});

/* ----------------------------------------
 * Resize → reset & replay
 * ---------------------------------------- */

let resizeTimer = null;

window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);

  resizeTimer = setTimeout(() => {
    counterRegistry.forEach((data, block) => {
      if (!data) return;

      data.tweens.forEach(t => t.kill());
      data.trigger?.kill();

      block.querySelectorAll('.counter').forEach(el => {
        el.dataset.counterInitialized = 'false';
      });

      const tweens = initCounters(block);

      ScrollTrigger.create({
        trigger: block,
        start: 'top center',
        once: true,
        onEnter: () => tweens.forEach(t => t.play()),
      });
    });
  }, 250);
});
